import { Component, computed, inject, OnInit, Signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TherapistClientStateService } from '@therapist/shared/services/state/therapist-client-state.service';
import { TherapistClientDto } from '@therapist/shared/models/client/therapist-client.dto';
import { Router, RouterLink } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { SekeletonLoadingComponent } from '@therapist/shared/components/sekeleton-loading/sekeleton-loading.component';
import { MultiSelect } from 'primeng/multiselect';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TherapyPlanTypeEnum, TherapyPlanTypeTranslations } from '@therapist/shared/models/therapy-plan/therapy-plan-type.enum';
import { JointListItemDto } from '@therapist/shared/models/diagnosis/joint-list-item.dto';
import { Button } from 'primeng/button';
import { ExerciseStateService } from '@therapist/shared/services/state/exercise-state.service';
import { JointStateService } from '@therapist/shared/services/state/joint-state.service';
import { TherapyPlanStateService } from '@therapist/shared/services/state/therapy-plan-state.service';
import { JointNameTranslationsPipe } from '@therapist/shared/pipes/joint-name-translations/joint-name-translations.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sana-tp-therapy-plan-add-page',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    NgTemplateOutlet,
    SekeletonLoadingComponent,
    MultiSelect,
    InputText,
    Select,
    Button
  ],
  templateUrl: './therapy-plan-add-new-page.component.html',
  styleUrl: './therapy-plan-add-new-page.component.scss',
  providers: [JointNameTranslationsPipe]
})
export class TherapyPlanAddNewPageComponent implements OnInit {

  readonly #fb = inject(FormBuilder);
  readonly #router = inject(Router);

  readonly #exerciseStateService = inject(ExerciseStateService);
  readonly #therapyPlanStateService = inject(TherapyPlanStateService);
  readonly #clientStateService = inject(TherapistClientStateService);
  readonly #jointStateService = inject(JointStateService);

  readonly #jointNameTranslationsPipe = inject(JointNameTranslationsPipe);

  selectedClient = this.#clientStateService.getSelectedClient() as Signal<TherapistClientDto>;
  isClientLoaded = this.#clientStateService.getIsClientLoaded();

  jointOptions = this.#jointStateService.getAllJoints();
  jointOptionsToSelect = computed(() => {
    return this.jointOptions().map(j => {
      return {
        id: j.id,
        type: this.#jointNameTranslationsPipe.transform(j.type)
      } as JointListItemDto;
    });
  });

  cachedTherapyPlanToAdd = this.#therapyPlanStateService.getCachedTherapyPlanToAdd();
  selectedJointsInState = this.#jointStateService.getSelectedJoints();

  previousTherapyPlan = this.#therapyPlanStateService.getPreviousTherapyPlan();

  form = this.#fb.group({
    selectedJoints: new FormControl<JointListItemDto[] | null>(null, [Validators.required]),
    diagnosisText: new FormControl<string | null>(null, [
      Validators.required,
      Validators.maxLength(10000)
    ]),
    therapyPlanType: new FormControl<TherapyPlanTypeEnum | null>(null, [Validators.required]),
    surgeryMethodText: new FormControl<string | null>(null, [Validators.maxLength(10000)])
  });

  constructor() {
    this.#jointStateService.loadAllJoints();

    const formControls = this.form.controls;
    formControls.therapyPlanType.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        const surgeryMethodTextControl = formControls.surgeryMethodText;

        if (value === TherapyPlanTypeEnum.Surgical) {
          surgeryMethodTextControl.setValidators([
            Validators.required,
            Validators.maxLength(10000)
          ]);
        } else {
          surgeryMethodTextControl.clearValidators();
        }

        surgeryMethodTextControl.updateValueAndValidity();
      });
  }

  ngOnInit() {
    const formControls = this.form.controls;

    if (this.cachedTherapyPlanToAdd() !== null) {
      const cache = this.cachedTherapyPlanToAdd()!;

      const cachedSelectedJoints = this.jointOptionsToSelect().filter(j => cache.selectedJointIds!.includes(j.id));
      formControls.selectedJoints.setValue(cachedSelectedJoints);

      formControls.diagnosisText.setValue(cache.diagnosisText!);
      formControls.therapyPlanType.setValue(cache.therapyPlanType!);
      formControls.surgeryMethodText.setValue(cache.surgeryMethodText!);
    }
  }

  routeToExercises(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const formControls = this.form.controls;

    const selectedJointsInForm = formControls.selectedJoints.value!;
    const selectedJointInFormIds = selectedJointsInForm.map(j => j.id);

    const hasJointSelectionChanged = this.hasJointSelectionChanged(selectedJointInFormIds, this.selectedJointsInState().map(joint => joint.id));

    if (hasJointSelectionChanged) {
      console.warn('removed joints from state and readded them');
      this.#jointStateService.removeAllSelectedJoints();
      this.#jointStateService.addSelectedJointList(selectedJointsInForm);

      this.#exerciseStateService.removeAll();
      this.#exerciseStateService.loadExercisesByJointIds(selectedJointInFormIds);
    }

    this.#therapyPlanStateService.setCachedTherapyPlanToAdd(
      {
        selectedJointIds: selectedJointInFormIds,
        diagnosisText: formControls.diagnosisText.value!,
        therapyPlanType: formControls.therapyPlanType.value!,
        surgeryMethodText: formControls.surgeryMethodText.value!
      }
    );

    this.#router.navigate([`client/${this.selectedClient().id}/therapy-plan/add/exercises`]);
  }

  private hasJointSelectionChanged(jointSelectionIds: string[], jointInStateIds: string[]): boolean {
    if (jointSelectionIds.length !== jointInStateIds.length) {
      return true;
    }

    const isEverySelectionIdInState = jointSelectionIds.every(selectedJointId => jointInStateIds.some(jointInStateId => jointInStateId === selectedJointId));
    return !isEverySelectionIdInState;
  }

  public applyPreviousPlan(): void {
    if (this.previousTherapyPlan() == null) {
      throw new Error('PreviousTherapyPlan is null');
    }

    const prevPlan = this.previousTherapyPlan()!;

    const controls = this.form.controls;
    controls.diagnosisText.setValue(prevPlan.diagnosisText);
    controls.therapyPlanType.setValue(prevPlan.therapyPlanType);
    controls.surgeryMethodText.setValue(prevPlan.surgeryMethodText);
    controls.selectedJoints.setValue(this.jointOptionsToSelect().filter(joint => prevPlan.jointIds.includes(joint.id)));
    this.#therapyPlanStateService.setIsPreviousTherapyPlanApplied(true);
  }

  protected readonly TherapyPlanTypeEnum = TherapyPlanTypeEnum;
  protected readonly TherapyPlanTypeTranslations = TherapyPlanTypeTranslations;
}
